import CalendarColor from 'owa-service/lib/contract/CalendarColor';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import LocalCacheForRemoteCalendarEntry from 'owa-service/lib/contract/LocalCacheForRemoteCalendarEntry';
import { generateColorScheme, generateDefaultColorSchemes } from 'owa-color-utils/lib/colorUtils';
import {
    getCalendarEntry,
    getCalendarIdByFolderId,
    CalendarId,
    getCalendarEntryByFolderId,
} from 'owa-calendar-cache';
import { getCategoryColorsForCategory, getMasterCategoryList } from 'owa-categories';
import { isFeatureEnabled } from 'owa-feature-flags';
import { trace } from 'owa-trace';
import { findInArray } from './findInArray';

const colorIndexOrder: number[] = [0, 5, 1, 3, 8, 6, 7, 2, 4];
let allColors: ColorScheme[];
let availableColors: ColorScheme[];

export function getAllColors(): ColorScheme[] {
    if (!allColors) {
        generateAllColors();
    }

    return allColors;
}

export function generateAllColors() {
    allColors = generateDefaultColorSchemes();
}

export function generateAvailableColors() {
    availableColors = generateAvailableColorsInOrder().reverse();
}

function getAvailableColors(): ColorScheme[] {
    if (!availableColors) {
        generateAvailableColors();
    }

    return availableColors;
}

/**
 * Cache of calendars assigned with colors
 */
let assignedCalendars: { [key: string]: ColorScheme } = {};

/**
 * Gets the name of the color
 * @param colorScheme the color scheme's name to retrieve
 */
export function getColorName(colorScheme: ColorScheme) {}

/**
 * Gets calendar color scheme by folder id
 * @returns the color scheme for the calendar
 */
export function getCalendarColorSchemeByFolderId(calendarFolderId: string): ColorScheme {
    let calendarId = getCalendarIdByFolderId(calendarFolderId);

    if (calendarId) {
        return getCalendarColorScheme(calendarId);
    } else {
        trace.warn(
            '[getCalendarColorSchemeByFolderId] Failed to retrieve the calendar Id while choosing color scheme'
        );
        return getColorSchemeForCalendarColor(CalendarColor.Auto);
    }
}

/**
 * Gets calendar color scheme for the surface item model. Uses the category color if item is categorized, otherwise uses the
 * parent folderId to grab the calendar color.
 * @returns the color scheme for the item
 */
export function getSurfaceItemModelColorScheme(model: CalendarEvent): ColorScheme {
    let categories = isFeatureEnabled('cal-categories') && model && model.Categories;
    return getColorScheme(model.ParentFolderId.Id, categories);
}

export function getPreviewColorScheme(model: CalendarEvent): ColorScheme {
    let colorScheme = getColorScheme(model.ParentFolderId.Id, null);
    return { ...colorScheme, ...{ calPrimary: colorScheme.calLighter } };
}

export function getFormsColorScheme(containerId: string, categories: string[]) {
    return isFeatureEnabled('cal-categories')
        ? getColorScheme(containerId, categories)
        : getCalendarColorSchemeByFolderId(containerId);
}

export function getColorScheme(containerId: string, categories: string[]) {
    // the color scheme for the item is dependent on the categories for the item
    let colorScheme = getCalendarColorSchemeByFolderId(containerId);
    if (isFeatureEnabled('cal-categories') && categories && categories.length > 0) {
        let categoryList = getMasterCategoryList();
        let calendarEntry = getCalendarEntryByFolderId(containerId);
        if (calendarEntry) {
            if ((calendarEntry as LocalCacheForRemoteCalendarEntry).RemoteCategories) {
                categoryList = (calendarEntry as LocalCacheForRemoteCalendarEntry).RemoteCategories
                    .MasterList;
            }

            let categoryColor = getCategoryColorsForCategory(categories[0], categoryList);
            colorScheme = generateColorScheme(categoryColor.iconColor);
        }
    }

    return colorScheme;
}

/**
 * Gets calendar color scheme by calendar id
 * @param calendarId The calendar id to retrieve the color for
 * @returns The color scheme for the calendar
 */
export function getCalendarColorScheme(calendarId: CalendarId): ColorScheme {
    let assignedCalendarColors = assignedCalendars[calendarId.id];
    if (!assignedCalendarColors) {
        assignedCalendarColors = refreshAssignedColorScheme(calendarId);
    }
    return assignedCalendarColors;
}

function getColorSchemeForCalendarColor(calendarColor: CalendarColor): ColorScheme {
    let assignedCalendarColors = null;
    if (calendarColor == undefined || calendarColor == CalendarColor.Auto) {
        assignedCalendarColors = getAvailableColors().pop();
    } else {
        assignedCalendarColors = getAllColors()[calendarColor];
        let availableColorsEntry = findInArray(getAvailableColors(), item => {
            return item == assignedCalendarColors;
        });
        if (availableColorsEntry) {
            getAvailableColors().splice(availableColorsEntry.index, 1);
        }
    }
    if (!availableColors.length) {
        availableColors = generateAvailableColorsInOrder().reverse();
    }
    return assignedCalendarColors;
}

/**
 * Generates color scheme array in color order
 * @returns all color schemes in color order
 */
export function generateAvailableColorsInOrder(): ColorScheme[] {
    return colorIndexOrder.map<ColorScheme>(colorIndex => {
        return getAllColors()[colorIndex];
    });
}

/** Refreshes the assigned color of a calendar based on the calendar color in the calendarsCache
 * @param calendarId id of calendar entry to refresh
 * @returns the color assigned to the calendar entry
 */
export function refreshAssignedColorScheme(calendarId: CalendarId): ColorScheme {
    let calendarEntry = getCalendarEntry({ id: calendarId });

    if (assignedCalendars[calendarId.id]) {
        // put the old color back into the available stack to be reused later
        getAvailableColors().push(assignedCalendars[calendarId.id]);

        // re-sort the available colors array
        let allColorsInOrder = generateAvailableColorsInOrder().reverse();
        availableColors = allColorsInOrder.filter(value => {
            return getAvailableColors().indexOf(value) > -1;
        });
    }

    let assignedCalendarColors;
    if (
        calendarEntry &&
        calendarEntry.HexColorString &&
        calendarEntry.HexColorString != '' &&
        getAllColors().filter(
            color => color.calPrimary.toLowerCase() == calendarEntry.HexColorString.toLowerCase()
        ).length > 0
    ) {
        assignedCalendarColors = generateColorScheme(calendarEntry.HexColorString);
    } else {
        assignedCalendarColors = getColorSchemeForCalendarColor(calendarEntry.CalendarColor);
    }

    assignedCalendars[calendarId.id] = assignedCalendarColors;

    return assignedCalendarColors;
}
